import React, { useState } from 'react';
import {
  FaFolder,
  FaFolderOpen,
  FaVideo,
  FaClock,
  FaChevronDown,
  FaChevronRight,
  FaEdit,
  FaTrash
} from 'react-icons/fa';
import './CourseStructurePreview.css';

const CourseStructurePreview = ({ modules, onEdit, onDelete }) => {
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedSubmodules, setExpandedSubmodules] = useState({});

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const toggleSubmodule = (submoduleId) => {
    setExpandedSubmodules(prev => ({
      ...prev,
      [submoduleId]: !prev[submoduleId]
    }));
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const calculateModuleDuration = (module) => {
    let totalDuration = 0;

    // Add direct lessons duration
    if (module.lessons) {
      totalDuration += module.lessons.reduce((sum, lesson) => sum + (lesson.duration || 0), 0);
    }

    // Add submodules lessons duration
    if (module.submodules) {
      module.submodules.forEach(submodule => {
        if (submodule.lessons) {
          totalDuration += submodule.lessons.reduce((sum, lesson) => sum + (lesson.duration || 0), 0);
        }
      });
    }

    return totalDuration;
  };

  const calculateTotalDuration = () => {
    return modules.reduce((total, module) => total + calculateModuleDuration(module), 0);
  };

  const countLessons = () => {
    let count = 0;
    modules.forEach(module => {
      if (module.lessons) count += module.lessons.length;
      if (module.submodules) {
        module.submodules.forEach(submodule => {
          if (submodule.lessons) count += submodule.lessons.length;
        });
      }
    });
    return count;
  };

  if (!modules || modules.length === 0) {
    return (
      <div className="structure-preview">
        <div className="preview-header">
          <h3>Course Structure Preview</h3>
        </div>
        <div className="preview-empty">
          <p>No modules added yet. Create your first module to see the structure.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="structure-preview">
      <div className="preview-header">
        <h3>Course Structure Preview</h3>
        <div className="course-stats">
          <span className="stat-item">
            <FaFolder /> {modules.length} Module{modules.length !== 1 ? 's' : ''}
          </span>
          <span className="stat-item">
            <FaVideo /> {countLessons()} Lesson{countLessons() !== 1 ? 's' : ''}
          </span>
          <span className="stat-item">
            <FaClock /> {formatDuration(calculateTotalDuration())}
          </span>
        </div>
      </div>

      <div className="preview-tree">
        {modules.map((module, moduleIndex) => {
          const isModuleExpanded = expandedModules[module.id] !== false;
          const moduleDuration = calculateModuleDuration(module);

          return (
            <div key={module.id} className="tree-module">
              <div className="tree-item module-item">
                <button
                  type="button"
                  className="tree-expand-btn"
                  onClick={() => toggleModule(module.id)}
                >
                  {isModuleExpanded ? <FaChevronDown /> : <FaChevronRight />}
                </button>
                <div className="tree-item-icon module-icon-preview">
                  {isModuleExpanded ? <FaFolderOpen /> : <FaFolder />}
                </div>
                <div className="tree-item-content">
                  <span className="tree-item-title">
                    {module.title || `Module ${moduleIndex + 1}`}
                  </span>
                  <span className="tree-item-meta">
                    {moduleDuration > 0 && (
                      <span className="duration-badge">
                        <FaClock /> {formatDuration(moduleDuration)}
                      </span>
                    )}
                  </span>
                </div>
              </div>

              {isModuleExpanded && (
                <div className="tree-children">
                  {/* Render Submodules */}
                  {module.submodules?.map((submodule, subIndex) => {
                    const isSubmoduleExpanded = expandedSubmodules[submodule.id] !== false;
                    const submoduleDuration = submodule.lessons?.reduce(
                      (sum, lesson) => sum + (lesson.duration || 0),
                      0
                    ) || 0;

                    return (
                      <div key={submodule.id} className="tree-submodule">
                        <div className="tree-item submodule-item">
                          <button
                            type="button"
                            className="tree-expand-btn"
                            onClick={() => toggleSubmodule(submodule.id)}
                          >
                            {isSubmoduleExpanded ? <FaChevronDown /> : <FaChevronRight />}
                          </button>
                          <div className="tree-item-icon submodule-icon-preview">
                            {isSubmoduleExpanded ? <FaFolderOpen /> : <FaFolder />}
                          </div>
                          <div className="tree-item-content">
                            <span className="tree-item-title">
                              {submodule.title || `Submodule ${subIndex + 1}`}
                            </span>
                            <span className="tree-item-meta">
                              {submoduleDuration > 0 && (
                                <span className="duration-badge">
                                  <FaClock /> {formatDuration(submoduleDuration)}
                                </span>
                              )}
                            </span>
                          </div>
                        </div>

                        {isSubmoduleExpanded && submodule.lessons && (
                          <div className="tree-children">
                            {submodule.lessons.map((lesson, lessonIndex) => (
                              <div key={lesson.id} className="tree-item lesson-item">
                                <div className="tree-item-spacer"></div>
                                <div className="tree-item-icon lesson-icon-preview">
                                  <FaVideo />
                                </div>
                                <div className="tree-item-content">
                                  <span className="tree-item-title">
                                    {lesson.title || `Lesson ${lessonIndex + 1}`}
                                  </span>
                                  <span className="tree-item-meta">
                                    {lesson.duration > 0 && (
                                      <span className="duration-badge">
                                        <FaClock /> {formatDuration(lesson.duration)}
                                      </span>
                                    )}
                                    {lesson.videoFileName && (
                                      <span className="video-badge">{lesson.videoFileName}</span>
                                    )}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Render Direct Lessons */}
                  {module.lessons?.map((lesson, lessonIndex) => (
                    <div key={lesson.id} className="tree-item lesson-item">
                      <div className="tree-item-spacer"></div>
                      <div className="tree-item-icon lesson-icon-preview">
                        <FaVideo />
                      </div>
                      <div className="tree-item-content">
                        <span className="tree-item-title">
                          {lesson.title || `Lesson ${lessonIndex + 1}`}
                        </span>
                        <span className="tree-item-meta">
                          {lesson.duration > 0 && (
                            <span className="duration-badge">
                              <FaClock /> {formatDuration(lesson.duration)}
                            </span>
                          )}
                          {lesson.videoFileName && (
                            <span className="video-badge">{lesson.videoFileName}</span>
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CourseStructurePreview;
